import React, { useState, useEffect } from 'react';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import '../../privacy-policy.css';

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState('section1');

  // Set SEO metadata on mount
  useEffect(() => {
    document.title = "Privacy Policy | PranaFlow Academy";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', "Read PranaFlow Academy’s Privacy Policy to understand how we collect, use, protect, and manage student, teacher, payment, WhatsApp, and website data.");
    }
    
    // Set canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://pranaflowacademy.com/privacy-policy');

    window.scrollTo(0, 0);
  }, []);

  // Track active section during scroll
  useEffect(() => {
    const sections = document.querySelectorAll('.privacy-doc-section');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-10% 0px -70% 0px' }
    );

    sections.forEach((sec) => observer.observe(sec));
    return () => sections.forEach((sec) => observer.unobserve(sec));
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  const tocItems = [
    { id: 'section1', label: '1. Business Information' },
    { id: 'section2', label: '2. Information We Collect' },
    { id: 'section3', label: '3. How We Use Information' },
    { id: 'section4', label: '4. WhatsApp Communication' },
    { id: 'section5', label: '5. Email Communication' },
    { id: 'section6', label: '6. Sharing of Information' },
    { id: 'section7', label: '7. Third-Party Services' },
    { id: 'section8', label: '8. Data Security' },
    { id: 'section9', label: '9. Data Retention' },
    { id: 'section10', label: '10. Student Accounts' },
    { id: 'section11', label: '11. Teacher & Employee Data' },
    { id: 'section12', label: '12. Cookies & Website Tracking' },
    { id: 'section13', label: '13. Children’s Privacy' },
    { id: 'section14', label: '14. Your Rights' },
    { id: 'section15', label: '15. Marketing Communication' },
    { id: 'section16', label: '16. Links to Other Websites' },
    { id: 'section17', label: '17. Policy Changes' },
    { id: 'section18', label: '18. Contact Us' }
  ];

  return (
    <div className="privacy-page-container">
      <PublicHeader />

      {/* Hero Header */}
      <section className="privacy-hero">
        <div className="container">
          <span className="privacy-last-updated-badge">Last Updated: 09 July 2026</span>
          <h1 className="privacy-hero-title">Privacy Policy</h1>
          <p className="privacy-hero-subtitle">
            Your privacy matters to us. Learn how PranaFlow Academy collects, uses, and protects your information.
          </p>
        </div>
      </section>

      {/* Micro Trust Cards */}
      <section className="privacy-trust-grid">
        <div className="privacy-trust-card">
          <div className="privacy-trust-icon-wrapper">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
          </div>
          <h4 className="privacy-trust-title">Data Protection</h4>
          <p className="privacy-trust-desc">We use secure access controls to restrict and protect your personal records.</p>
        </div>
        <div className="privacy-trust-card">
          <div className="privacy-trust-icon-wrapper">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
              <line x1="1" y1="10" x2="23" y2="10"></line>
            </svg>
          </div>
          <h4 className="privacy-trust-title">Secure Payments</h4>
          <p className="privacy-trust-desc">Processed via secure SSL payment gateways (Razorpay). No CVV/PIN is saved.</p>
        </div>
        <div className="privacy-trust-card">
          <div className="privacy-trust-icon-wrapper">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
          </div>
          <h4 className="privacy-trust-title">WhatsApp & Email</h4>
          <p className="privacy-trust-desc">Alerts for registrations, batches, fee transactions, and support enquiries.</p>
        </div>
        <div className="privacy-trust-card">
          <div className="privacy-trust-icon-wrapper">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
          <h4 className="privacy-trust-title">Academic Records</h4>
          <p className="privacy-trust-desc">Course progress tracking, exam logs, certificate issuance, and teacher apps.</p>
        </div>
      </section>

      {/* Main Split Content Layout */}
      <main className="privacy-split-wrapper">
        {/* Table of Contents sidebar */}
        <aside className="privacy-sidebar-nav">
          <h4 className="privacy-sidebar-title">Table of Contents</h4>
          <ul className="privacy-toc-list">
            {tocItems.map((item) => (
              <li
                key={item.id}
                className={`privacy-toc-item ${activeSection === item.id ? 'active' : ''}`}
              >
                <button type="button" onClick={() => scrollToSection(item.id)}>
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Right side content */}
        <article className="privacy-content-card">
          <div className="privacy-intro-section">
            <p>Welcome to PranaFlow Academy.</p>
            <p>
              This Privacy Policy explains how PranaFlow Academy collects, uses, stores, and protects your personal information when you visit our website, submit an enquiry, register for a course, make a payment, use our student/teacher/admin dashboard, or communicate with us through WhatsApp, email, phone, or other channels.
            </p>
            <p>By using our website or services, you agree to the practices described in this Privacy Policy.</p>
          </div>

          {/* Section 1 */}
          <section id="section1" className="privacy-doc-section">
            <h2>1. Business Information</h2>
            <div className="privacy-table-wrapper">
              <table className="privacy-table">
                <tbody>
                  <tr>
                    <th>Brand Name</th>
                    <td>PranaFlow Academy</td>
                  </tr>
                  <tr>
                    <th>Website URL</th>
                    <td><a href="https://pranaflowacademy.com" target="_blank" rel="noopener noreferrer" style={{ color: '#B9853D', textDecoration: 'none', fontWeight: 600 }}>https://pranaflowacademy.com</a></td>
                  </tr>
                  <tr>
                    <th>Email Address</th>
                    <td><a href="mailto:pranaflowacademy@gmail.com" style={{ color: '#B9853D', textDecoration: 'none', fontWeight: 600 }}>pranaflowacademy@gmail.com</a></td>
                  </tr>
                  <tr>
                    <th>WhatsApp/Contact</th>
                    <td>+91 9548354084</td>
                  </tr>
                  <tr>
                    <th>Business Type</th>
                    <td>Online and offline yoga, wellness, teacher training, and educational programs.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <hr className="privacy-divider" />

          {/* Section 2 */}
          <section id="section2" className="privacy-doc-section">
            <h2>2. Information We Collect</h2>
            <p>We may collect the following information from students, teachers, employees, applicants, and website visitors:</p>
            
            <p style={{ fontWeight: 700, color: '#0D443E', marginBottom: '8px' }}>Personal Information</p>
            <p>We may collect:</p>
            <ul>
              <li>Full name</li>
              <li>Email address</li>
              <li>Mobile number / WhatsApp number</li>
              <li>Country code</li>
              <li>Address or location details</li>
              <li>Date of birth, if required for registration or certification</li>
              <li>Father’s name or guardian details, if required for student records</li>
              <li>Profile photo, if uploaded</li>
              <li>Course enquiry details</li>
              <li>Course registration details</li>
              <li>Batch and enrollment details</li>
            </ul>

            <p style={{ fontWeight: 700, color: '#0D443E', marginBottom: '8px', marginTop: '20px' }}>Course and Academic Information</p>
            <p>We may collect:</p>
            <ul>
              <li>Selected course name</li>
              <li>Batch details</li>
              <li>Attendance or class participation records</li>
              <li>Uploaded assignments, exam attempts, marks, and results</li>
              <li>Certificate-related information</li>
              <li>Teacher reviews and feedback</li>
            </ul>

            <p style={{ fontWeight: 700, color: '#0D443E', marginBottom: '8px', marginTop: '20px' }}>Payment Information</p>
            <p>When you make a payment, we may collect:</p>
            <ul>
              <li>Payment amount</li>
              <li>Payment status</li>
              <li>Payment date and time</li>
              <li>Transaction ID or payment reference</li>
              <li>Course fee, discount, installment, and balance details</li>
            </ul>
            <p>We do not store your full card number, UPI PIN, CVV, net banking password, or sensitive bank login details. Payments may be processed through secure third-party payment gateways such as Razorpay or other payment service providers.</p>

            <p style={{ fontWeight: 700, color: '#0D443E', marginBottom: '8px', marginTop: '20px' }}>Communication Information</p>
            <p>When you contact us through WhatsApp, email, phone, enquiry form, admission form, or support channels, we may collect:</p>
            <ul>
              <li>Your message content</li>
              <li>Contact details</li>
              <li>Enquiry or support request details</li>
              <li>Communication history related to your course, payment, admission, or support request</li>
            </ul>

            <p style={{ fontWeight: 700, color: '#0D443E', marginBottom: '8px', marginTop: '20px' }}>Technical Information</p>
            <p>When you visit our website, we may automatically collect basic technical data such as:</p>
            <ul>
              <li>IP address</li>
              <li>Browser type</li>
              <li>Device type</li>
              <li>Pages visited</li>
              <li>Date and time of visit</li>
              <li>Basic usage data</li>
              <li>Cookies or similar tracking information, if enabled</li>
            </ul>
          </section>

          <hr className="privacy-divider" />

          {/* Section 3 */}
          <section id="section3" className="privacy-doc-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use your information for the following purposes:</p>
            <ul>
              <li>To respond to course enquiries</li>
              <li>To process course admissions and enrollments</li>
              <li>To create and manage student accounts</li>
              <li>To create and manage teacher or employee accounts</li>
              <li>To provide access to online classes, recordings, course materials, exams, and certificates</li>
              <li>To manage payments, installments, invoices, and fee reminders</li>
              <li>To send payment confirmations and enrollment updates</li>
              <li>To send WhatsApp, email, or phone notifications related to your course</li>
              <li>To verify teacher applications, profiles, and qualifications</li>
              <li>To improve our website, services, support, and learning experience</li>
              <li>To prevent fraud, misuse, or unauthorized access</li>
              <li>To comply with applicable legal, tax, accounting, and regulatory requirements</li>
            </ul>
          </section>

          <hr className="privacy-divider" />

          {/* Section 4 */}
          <section id="section4" className="privacy-doc-section">
            <h2>4. WhatsApp Communication</h2>
            <p>PranaFlow Academy may use WhatsApp to send important communication such as:</p>
            <ul>
              <li>Course enquiry confirmation</li>
              <li>Admission confirmation</li>
              <li>Enrollment updates</li>
              <li>Payment confirmation</li>
              <li>Fee due reminders</li>
              <li>Class or batch updates</li>
              <li>Teacher or staff notifications</li>
              <li>Support messages</li>
            </ul>
            <p>We may use the WhatsApp Business Platform / WhatsApp Cloud API provided by Meta to send these messages.</p>
            <p>By submitting your mobile number or WhatsApp number on our website, you agree that PranaFlow Academy may contact you through WhatsApp for course, payment, enrollment, support, and service-related communication.</p>
            <p>You may request to stop receiving non-essential WhatsApp communication by contacting us at:</p>
            <p style={{ fontWeight: 600, color: '#0D443E' }}>
              Email: pranaflowacademy@gmail.com<br />
              WhatsApp: +91 9548354084
            </p>
          </section>

          <hr className="privacy-divider" />

          {/* Section 5 */}
          <section id="section5" className="privacy-doc-section">
            <h2>5. Email Communication</h2>
            <p>We may send you emails for:</p>
            <ul>
              <li>Course enquiry confirmation</li>
              <li>Admission or enrollment confirmation</li>
              <li>Payment confirmation</li>
              <li>Login or account-related communication</li>
              <li>Course updates</li>
              <li>Certificates</li>
              <li>Support responses</li>
              <li>Important academy announcements</li>
            </ul>
            <p>You can contact us anytime if you want to update your email address or stop receiving non-essential communication.</p>
          </section>

          <hr className="privacy-divider" />

          {/* Section 6 */}
          <section id="section6" className="privacy-doc-section">
            <h2>6. Sharing of Information</h2>
            <p>We do not sell your personal information.</p>
            <p>We may share limited information only when necessary with:</p>
            <ul>
              <li>Payment gateway providers for payment processing</li>
              <li>WhatsApp Business Platform / Meta for WhatsApp communication</li>
              <li>Email service providers for sending emails</li>
              <li>Hosting, database, and technology service providers</li>
              <li>Teachers, staff, or admin team members who need access to provide services</li>
              <li>Legal, tax, government, or regulatory authorities, if required by law</li>
            </ul>
            <p>We only share information that is necessary for the related service.</p>
          </section>

          <hr className="privacy-divider" />

          {/* Section 7 */}
          <section id="section7" className="privacy-doc-section">
            <h2>7. Payment Gateway and Third-Party Services</h2>
            <p>Our website may use third-party services such as payment gateways, hosting providers, email providers, WhatsApp API providers, analytics tools, or other technical service providers.</p>
            <p>These third-party services may process your information according to their own privacy policies and security practices.</p>
            <p>PranaFlow Academy is not responsible for the privacy practices of third-party websites or platforms that are not controlled by us.</p>
          </section>

          <hr className="privacy-divider" />

          {/* Section 8 */}
          <section id="section8" className="privacy-doc-section">
            <h2>8. Data Security</h2>
            <p>We take reasonable technical and organizational steps to protect your personal information from unauthorized access, misuse, loss, alteration, or disclosure.</p>
            <p>These steps may include:</p>
            <ul>
              <li>Secure server access</li>
              <li>Password-protected dashboards</li>
              <li>Restricted admin access</li>
              <li>Secure payment processing through third-party payment gateways</li>
              <li>Limited access to student, teacher, and payment records</li>
              <li>Regular monitoring and maintenance of website systems</li>
            </ul>
            <p>However, no online platform, website, or internet-based service can guarantee 100% security.</p>
          </section>

          <hr className="privacy-divider" />

          {/* Section 9 */}
          <section id="section9" className="privacy-doc-section">
            <h2>9. Data Retention</h2>
            <p>We keep your personal information only for as long as necessary for:</p>
            <ul>
              <li>Providing our services</li>
              <li>Maintaining student and course records</li>
              <li>Issuing certificates</li>
              <li>Managing payments and invoices</li>
              <li>Resolving disputes or support issues</li>
              <li>Meeting legal, tax, accounting, or regulatory requirements</li>
            </ul>
            <p>When your information is no longer required, we may delete, archive, or anonymize it in a secure manner.</p>
          </section>

          <hr className="privacy-divider" />

          {/* Section 10 */}
          <section id="section10" className="privacy-doc-section">
            <h2>10. Student Accounts and Dashboard Data</h2>
            <p>If you register as a student, we may store and display information in your student dashboard, such as:</p>
            <ul>
              <li>Name</li>
              <li>Email</li>
              <li>Phone number</li>
              <li>Enrolled course</li>
              <li>Batch details</li>
              <li>Payment status</li>
              <li>Installment status</li>
              <li>Course access status</li>
              <li>Exam results</li>
              <li>Certificate status</li>
              <li>Teacher details</li>
              <li>Class recordings and course materials available to you</li>
            </ul>
            <p>You are responsible for keeping your login details confidential. Please do not share your password with anyone.</p>
          </section>

          <hr className="privacy-divider" />

          {/* Section 11 */}
          <section id="section11" className="privacy-doc-section">
            <h2>11. Teacher and Employee Data</h2>
            <p>If you apply as a teacher or employee, we may collect and store:</p>
            <ul>
              <li>Name</li>
              <li>Email</li>
              <li>Phone number</li>
              <li>Profile photo</li>
              <li>Bio</li>
              <li>Teaching experience</li>
              <li>Expertise and specialization</li>
              <li>Certifications</li>
              <li>Professional links</li>
              <li>Yoga school affiliation details</li>
              <li>Verification and reference information</li>
              <li>Uploaded profile details for admin review</li>
            </ul>
            <p>Some approved teacher profile information may be visible to students or website visitors. Private verification details will only be visible to authorized admin users and the concerned teacher/employee.</p>
          </section>

          <hr className="privacy-divider" />

          {/* Section 12 */}
          <section id="section12" className="privacy-doc-section">
            <h2>12. Cookies and Website Tracking</h2>
            <p>Our website may use cookies or similar technologies to:</p>
            <ul>
              <li>Improve website performance</li>
              <li>Remember user preferences</li>
              <li>Support login sessions</li>
              <li>Understand website usage</li>
              <li>Improve security and user experience</li>
            </ul>
            <p>You can control or disable cookies through your browser settings. Some website features may not work properly if cookies are disabled.</p>
          </section>

          <hr className="privacy-divider" />

          {/* Section 13 */}
          <section id="section13" className="privacy-doc-section">
            <h2>13. Children’s Privacy</h2>
            <p>Our services are generally intended for students, teachers, and learners who can provide valid consent or have guardian permission where required.</p>
            <p>If a minor uses our services, the registration should be completed with the consent of a parent or legal guardian.</p>
            <p>We do not knowingly collect personal information from children without appropriate consent. If you believe a child has submitted information without guardian consent, please contact us so we can review and take appropriate action.</p>
          </section>

          <hr className="privacy-divider" />

          {/* Section 14 */}
          <section id="section14" className="privacy-doc-section">
            <h2>14. Your Rights</h2>
            <p>You may contact us to request:</p>
            <ul>
              <li>Access to your personal information</li>
              <li>Correction of incorrect information</li>
              <li>Update of your contact details</li>
              <li>Deletion of information, where legally possible</li>
              <li>Withdrawal from non-essential communication</li>
              <li>Clarification about how your data is used</li>
            </ul>
            <p>To make a request, contact us at:</p>
            <p style={{ fontWeight: 600, color: '#0D443E' }}>
              Email: pranaflowacademy@gmail.com<br />
              WhatsApp: +91 9548354084
            </p>
            <p>We may need to verify your identity before processing your request.</p>
          </section>

          <hr className="privacy-divider" />

          {/* Section 15 */}
          <section id="section15" className="privacy-doc-section">
            <h2>15. Marketing Communication</h2>
            <p>We may send you course updates, offers, new batch announcements, or academy-related information through WhatsApp, email, phone, or other communication channels.</p>
            <p>You can opt out of non-essential promotional communication anytime by contacting us.</p>
            <p>Even if you opt out of promotional messages, we may still send important service-related messages such as payment confirmation, enrollment updates, class updates, account alerts, or certificate information.</p>
          </section>

          <hr className="privacy-divider" />

          {/* Section 16 */}
          <section id="section16" className="privacy-doc-section">
            <h2>16. Links to Other Websites</h2>
            <p>Our website may contain links to third-party websites, payment gateways, social media pages, YouTube videos, or external resources.</p>
            <p>We are not responsible for the content, privacy policies, or practices of third-party websites.</p>
            <p>Please review their privacy policies before sharing your personal information with them.</p>
          </section>

          <hr className="privacy-divider" />

          {/* Section 17 */}
          <section id="section17" className="privacy-doc-section">
            <h2>17. Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time.</p>
            <p>When we update this policy, we will revise the “Last Updated” date at the top of this page.</p>
            <p>We encourage users to review this page regularly to stay informed about how we protect their information.</p>
          </section>

          <hr className="privacy-divider" />

          {/* Section 18 */}
          <section id="section18" className="privacy-doc-section">
            <h2>18. Contact Us</h2>
            <p>For any questions, concerns, corrections, or data-related requests, you can contact us at:</p>
            
            <div className="privacy-cta-section">
              <h4 className="privacy-cta-title">Have questions about your data?</h4>
              <p className="privacy-cta-desc">Feel free to connect with our support team via email or WhatsApp anytime.</p>
              
              <div className="privacy-cta-buttons">
                <a href="mailto:pranaflowacademy@gmail.com" className="privacy-cta-btn email">
                  <svg viewBox="0 0 24 24">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                  <span>pranaflowacademy@gmail.com</span>
                </a>
                <a href="https://wa.me/919548354084" target="_blank" rel="noopener noreferrer" className="privacy-cta-btn whatsapp">
                  <svg viewBox="0 0 24 24">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.731-1.456L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.859-4.407 9.862-9.823.002-2.623-1.017-5.09-2.871-6.944-1.853-1.854-4.321-2.87-6.944-2.872-5.442 0-9.863 4.41-9.865 9.827-.001 1.771.468 3.498 1.36 5.03l-.993 3.624 3.731-.977c-.105-.045-.2-.09-.285-.118zm10.741-7.234c-.29-.145-1.716-.848-1.982-.944-.266-.096-.46-.145-.653.145-.194.291-.75.944-.919 1.138-.169.194-.339.218-.629.073-.29-.145-1.226-.452-2.335-1.441-.864-.771-1.447-1.724-1.616-2.015-.17-.29-.018-.447.127-.591.131-.13.29-.339.436-.509.145-.17.194-.291.29-.485.097-.194.049-.364-.025-.509-.073-.145-.653-1.575-.895-2.156-.236-.569-.475-.491-.653-.5H6.99c-.194 0-.509.073-.775.364-.266.291-1.017.994-1.017 2.424s1.041 2.812 1.186 3.006c.145.194 2.049 3.129 4.965 4.385.694.299 1.236.478 1.658.613.697.221 1.332.19 1.833.115.559-.084 1.716-.703 1.958-1.382.242-.679.242-1.261.169-1.382-.072-.12-.266-.193-.556-.339z"/>
                  </svg>
                  <span>WhatsApp Chat</span>
                </a>
              </div>
            </div>
          </section>
        </article>
      </main>

      <PublicFooter />
    </div>
  );
};

export default PrivacyPolicy;
